import { useState } from "react";
import { RiskConsequenceSaveModel } from "./riskConsequenceDialog/RiskConsequenceDialog.types";
import { RiskConsequencesViewModel, RiskConsequenceViewModel } from "./RiskConsequences.types";
import { getRiskConsequenceCategoryOptions } from "./RiskConsequence.utils";
import { useDotLegalQuery, useDotLegalQueryClient } from "@common/hooks/dotLegalReactQuery";
import { useTranslation } from "@localization/useTranslation";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import { useRiskAreas } from "@common/hooks/useSelectableItemQueries";
import { useFilterState } from "@common/hooks/useFilterState";
import { applyClientFilters, withOperators } from "@common/components/filterBar/filterOperatorMatching";
import { FilterOption, FilterOptionType } from "@common/components/filterPopover/FilterPopover.types";

export function toSaveModel(riskConsequence: RiskConsequenceViewModel): RiskConsequenceSaveModel {
    return {
        ...riskConsequence,
        category: riskConsequence.category.toString(),
        riskAreaIds: riskConsequence.riskAreas.map((riskArea) => riskArea.id),
    };
}

export function useRiskConsequencesDataMapping() {
    const queryClient = useDotLegalQueryClient();
    const { translateString, translateRiskConsequenceCategory } = useTranslation();
    const { customerId } = useUserContext();
    const { riskAreas } = useRiskAreas();
    const [showRiskConsequenceDialog, setShowRiskConsequenceDialog] = useState(false);
    const [showCatalogDialog, setShowCatalogDialog] = useState(false);
    const [riskConsequenceForDeletion, setRiskConsequenceForDeletion] = useState<RiskConsequenceViewModel | undefined>(undefined);
    const [selectedRiskConsequence, setSelectedRiskConsequence] = useState<RiskConsequenceSaveModel | undefined>(undefined);

    const { selectFilters, filterOperators, onFilterChange, pinnedFilterIds, onPinnedFilterIdsChange } = useFilterState(
        `risk-consequences-overview-${customerId}`
    );

    const url = "/risk-consequences";
    const catalogUrl = "/risk-consequences/catalog";
    const { isLoading, data } = useDotLegalQuery<RiskConsequencesViewModel>(url);

    const hasRiskConsequences = isLoading || (data?.items.length ?? 0) > 0;

    const hasCatalogItemsToImport = data?.hasCatalogItemsToImport ?? false;

    const allRiskAreaIds = riskAreas?.map((riskArea) => riskArea.id) ?? [];

    const categoryOptions = getRiskConsequenceCategoryOptions(translateRiskConsequenceCategory);

    const filteredData = applyClientFilters(
        data?.items,
        [
            {
                id: "riskAreas",
                candidate: (consequence) => (consequence.appliesToAllRiskAreas ? allRiskAreaIds : consequence.riskAreas.map((riskArea) => riskArea.id)),
            },
            {
                id: "category",
                candidate: (consequence) => [consequence.category.toString()],
            },
        ],
        selectFilters,
        {},
        filterOperators
    );

    const getFilterDataOptions = (): Array<FilterOption> => {
        return withOperators(
            [
                {
                    type: FilterOptionType.SelectField,
                    id: "category",
                    value: selectFilters.category ?? [],
                    label: translateString("category"),
                    fixedData: categoryOptions,
                },
                {
                    type: FilterOptionType.SelectField,
                    id: "riskAreas",
                    value: selectFilters.riskAreas ?? [],
                    label: translateString("riskAreas"),
                    fixedData: riskAreas ?? [],
                },
            ],
            filterOperators
        );
    };

    const onSave = () => {
        queryClient.refetchQueries(url);
        queryClient.refetchQueries(catalogUrl);
    };

    return {
        pinnedFilterIds,
        onPinnedFilterIdsChange,
        isLoading,
        data: filteredData,
        hasRiskConsequences,
        hasCatalogItemsToImport,
        getFilterDataOptions,
        mapSearchFilter: onFilterChange,
        showRiskConsequenceDialog,
        setShowRiskConsequenceDialog,
        showCatalogDialog,
        setShowCatalogDialog,
        riskConsequenceForDeletion,
        setRiskConsequenceForDeletion,
        onSave,
        selectedRiskConsequence,
        setSelectedRiskConsequence,
    };
}
